import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';
import rimg from '@pixelunion/rimg-shopify';
import ScrollLock from '../helpers/ScrollLock';

export default class PredictiveSearch {
  constructor() {
    if (document.body.dataset.predictiveSearch === 'false') return;
    this.events = new EventHandler();
    this.searchOverlay = document.querySelector('[data-search-overlay]');
    this.search = document.querySelector('[data-search]');
    this.searchForm = document.querySelector('[data-search-form]');
    this.resultsWrapper = document.querySelector('[data-results-wrapper]');
    this.searchTerms = document.querySelector('[data-predictive-search-terms]');
    this.openSearchButtons = document.querySelectorAll('[data-search-open]');
    this.closeSearchButton = document.querySelector('[data-close-search]');

    this.isOpen = false;
    this.trigger = null;
    this.timer = null;
    this.getResults = this.getResults.bind(this);

    this.animateSearchOverlay = transition({ el: this.searchOverlay, state: 'closed' });
    this.animateSearch = transition({ el: this.search, state: 'closed' });

    if (document.querySelector('[data-side-search-results]')) {
      this.sideSearchResults = document.querySelector('[data-side-search-results]');
    }

    this._setUpEventListeners();
  }

  _setUpEventListeners() {
    this.openSearchButtons.forEach(openButton => {
      this.events.register(openButton, 'click', e => {
        e.preventDefault();
        this.openSearch();
        this.trigger = openButton;
      });
    });

    this.events.register(this.searchForm, 'submit', e => {
      if (this.searchTerms.value.length === 0) {
        e.preventDefault();
      }
    });

    this.events.register(this.closeSearchButton, 'click', e => {
      e.preventDefault();
      this.closeSearch();
    });

    this.events.register(this.search, 'focusout', e => {
      if (!(e.relatedTarget && e.relatedTarget.closest('[data-search]'))) {
        this.closeSearch();
      }
    });
  }

  _closeEsc(e) {
    if (e.key === 'Escape') {
      this.searchTerms.value = '';
      e.stopPropagation();
      this.closeSearch();
    }
  }

  openSearch() {
    if (this.isOpen) return;

    this.animateSearchOverlay.animateTo('open');
    this.animateSearch.animateTo('open').then(() => {
      this.searchTerms.focus();
      if (this.searchTerms.value.length >= 1) {
        // Set the cursor at the end of the input field for better UX,
        // otherwise it may be at the beginning of the input field when the input gets focus.
        const strLen = this.searchTerms.value.length;
        this.searchTerms.setSelectionRange(strLen, strLen);
        // Search results should appear when the search dropdown is closed and reopened.
        this.getResults();
      }
      this.isOpen = true;
    });

    this.events.register(this.searchTerms, 'keyup', e => {
      clearTimeout(this.timer);
      // Check if word is greater than 1 letter and we are not trying to tab
      if (this.searchTerms.value.length >= 1 && e.keyCode !== 9) {
        this.timer = setTimeout(this.getResults, 300);
      } else if (this.searchTerms.value === '') {
        this.clearResults();
      }
    });

    this.closeEscEvent = this.events.register(window, 'keydown', e => this._closeEsc(e));
  }

  closeSearch() {
    if (!this.isOpen) return;

    this.events.unregister(this.searchOverlay);
    this.events.unregister(this.searchTerms);
    this.events.unregister(this.closeEscEvent);

    this.animateSearch.animateTo('closed')
      .then(() => {
        ScrollLock.unlock();
        this.trigger.focus();
        this.clearResults();
        this.animateSearchOverlay.animateTo('closed');
        this.isOpen = false;
      });
  }

  getResults() {
    fetch(`${window.PXUTheme.routes.predictive_search_url}?q=${this.searchTerms.value}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(html => {
        this.resultsWrapper.innerHTML = html;
        rimg.watch(this.resultsWrapper);
        ScrollLock.lock(this.resultsWrapper);
      })
      .catch(error => {
        throw error;
      });
  }

  clearResults() {
    const el = this.resultsWrapper;

    // This should be more performant than `innerHTMl = ""`
    while (el.firstChild) el.removeChild(el.firstChild);
  }
}
